import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {ComboBox, FlexColumn, NumberField, Text} from '@forecast-it/design-system';
import {AddRowButton, EmptyEstimates, RemoveRowButton} from './EstimateLineParts';
import {blankTimeEstimate, Option, optionsForRow, TimeEstimateDraft, toNullableNumber} from './baselineTemplateDraft';

const Grid = Styled.div`
	display: grid;
	grid-template-columns: minmax(200px, 1fr) 140px 40px;
	gap: 8px 12px;
	align-items: start;
`;

type Props = {
	phaseIndex: number;
	rows: TimeEstimateDraft[];
	roles: Option[];
	/** Disabled roles that loaded rows still hold, keyed by row; each is offered on its own row only. */
	heldRoles?: Readonly<Record<string, Option>>;
	fieldError: (path: string) => string | undefined;
	onChange: (rows: TimeEstimateDraft[], changedPath?: string) => void;
};

export const TimeEstimateLines = ({phaseIndex, rows, roles, heldRoles, fieldError, onChange}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const roleHeaderId = `baseline-template-phase-${phaseIndex}-role-header`;
	const cy = (rowIndex: number, part: string) => `baseline-template-phase-${phaseIndex}-time-${rowIndex}-${part}`;
	const update = (key: string, patch: Partial<TimeEstimateDraft>, field: string) =>
		onChange(
			rows.map(row => (row.key === key ? {...row, ...patch} : row)),
			`${key}.${field}`
		);

	return (
		<FlexColumn gap="s">
			{rows.length === 0 ? (
				<EmptyEstimates message={formatMessage({id: 'deliverable.no_time_estimates_no_deliverables'})} />
			) : (
				<Grid>
					<span id={roleHeaderId}>
						<Text color="auxiliary">{formatMessage({id: 'deliverable.columns.role'})}</Text>
					</span>
					<Text color="auxiliary">{formatMessage({id: 'deliverable.columns.hours'})}</Text>
					<span />
					{rows.map((row, rowIndex) => {
						return (
							<React.Fragment key={row.key}>
								<div data-field-path={`${row.key}.roleId`}>
									<ComboBox
										aria-labelledby={roleHeaderId}
										data-cy={cy(rowIndex, 'role')}
										items={optionsForRow(roles, heldRoles, row.key)}
										value={row.roleId}
										onChange={(id: string | null) => update(row.key, {roleId: id ?? null}, 'roleId')}
										menuTrigger="focus"
										width={ComboBox.WIDTH.FLEX}
										hasError={!!fieldError(`${row.key}.roleId`)}
										errorText={fieldError(`${row.key}.roleId`)}
									>
										{(option: Option) => (
											<ComboBox.Item textValue={option.label}>{option.label}</ComboBox.Item>
										)}
									</ComboBox>
								</div>
								<div data-field-path={`${row.key}.hours`}>
									<NumberField
										aria-label={formatMessage({id: 'deliverable.columns.hours'})}
										data-cy={cy(rowIndex, 'hours')}
										value={row.hours ?? Number.NaN}
										onChange={(value: number) => update(row.key, {hours: toNullableNumber(value)}, 'hours')}
										allowEmpty
										minValue={0}
										minimumFractionDigits={0}
										maximumFractionDigits={2}
										hasError={!!fieldError(`${row.key}.hours`)}
										errorText={fieldError(`${row.key}.hours`)}
									/>
								</div>
								<RemoveRowButton
									label={formatMessage({id: 'deliverable.columns.delete_time_estimate'})}
									data-cy={cy(rowIndex, 'remove')}
									onPress={() => onChange(rows.filter(candidate => candidate.key !== row.key))}
								/>
							</React.Fragment>
						);
					})}
				</Grid>
			)}
			<AddRowButton
				label={formatMessage({id: 'deliverable.add_time_estimate'})}
				data-cy={`baseline-template-phase-${phaseIndex}-add-time`}
				onPress={() => onChange([...rows, blankTimeEstimate()])}
			/>
		</FlexColumn>
	);
};
